import {
    Box,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { dataFromEdges } from "lib/permissions/helpers"
import { PagingComponent } from ".."
import TableSkeleton from "minimals-template/components/table/TableSkeleton"
import { Else } from "lib/@components/else"
import { If } from "lib/@components/switch"

export function PagedTable({ skeleton, ...props }) {
    return (
        <PagingComponent
            skeleton={skeleton ?? <TableSkeleton rowCount={5} dense />}
            component={LocalPagedTable}
            {...props}
        />
    )
}

function LocalPagedTable({
    headers,
    RowComponent = PlaceHolderRow,
    queryResults,
    orderBy,
    sortOrder,
    handleSortBy,
    onSelectRow,
    size,
    hasSelector,
    selectorProps,
    tableSx,
    ...props
}) {
    const rows = dataFromEdges(queryResults)

    const {
        onSelectAll = undefined,
        onSelectIndividual = undefined,
        selectedItems = undefined,
        rowIdentifier = undefined,
    } = selectorProps ?? {}

    let allSelected = false
    if (hasSelector) {
        allSelected = selectedItems && rows.every((rowItem) => selectedItems.includes(rowItem[rowIdentifier]))
        headers = [
            {
                id: "selector",
                isSelector: true,
                onSelect: handleSelectHeader,
                isChecked: allSelected,
            },
            ...headers,
        ]
    }
    return (
        <TableContainer sx={{ width: "100%", overflow: "auto !important" }}>
            <Table size={size} sx={tableSx ? tableSx : {}}>
                {headers && (
                    <PagedTableHeader
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        handleSortBy={handleSortBy}
                        headers={headers}
                        headerStyles={props.headerStyles}
                    />
                )}
                <TableBody>
                    {rows?.map((row, ix) => (
                        <RowComponent
                            key={row[rowIdentifier] ?? ix}
                            row={row}
                            onSelectRow={onSelectRow}
                            onSelectorClicked={onSelectIndividual}
                            isSelected={selectedItems?.includes(row[rowIdentifier])}
                            {...props}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )

    function handleSelectHeader() {
        const selection = {
            isSelecting: !allSelected,
            items: allSelected
                ? rows.map((rowItem) => rowItem[rowIdentifier])
                : rows
                      .filter((rowItem) => !selectedItems.includes(rowItem[rowIdentifier]))
                      .map((rowItem) => rowItem[rowIdentifier]),
        }
        onSelectAll(selection)
    }
}

function PagedTableHeader({ headers, orderBy, sortOrder, handleSortBy, headerStyles }) {
    return (
        <>
            {headers && (
                <TableHead>
                    <TableRow sx={{ ...headerStyles }}>
                        {headers.map((header) => {
                            const canOrderBy = header.canOrderBy ?? true
                            const noPadding = header.noPadding ? { p: "0 !important" } : {}
                            return header.isSelector ? (
                                <TableCell key={header.id}>
                                    <Checkbox onClick={header.onSelect} checked={header.isChecked} />
                                </TableCell>
                            ) : (
                                <TableCell
                                    key={header.id}
                                    style={{ boxShadow: "none" }}
                                    sx={{ backgroundColor: "primary", ...noPadding }}
                                    align={header.align || "left"}
                                    padding={header.padding ? "none" : "normal"}
                                    sortDirection={orderBy === header.id ? sortOrder : false}
                                >
                                    <If value={canOrderBy}>
                                        <TableSortLabel
                                            active={orderBy === header.id}
                                            direction={orderBy === header.id ? sortOrder : "asc"}
                                            onClick={() => handleSortBy(header.id)}
                                            data-cy="sort-on-label"
                                        >
                                            {header.label}
                                            {orderBy === header.id ? (
                                                <Box component="span" sx={{ ...visuallyHidden }}>
                                                    {sortOrder === "desc" ? "sorted descending" : "sorted ascending"}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                        <Else>{header.label}</Else>
                                    </If>
                                </TableCell>
                            )
                        })}
                    </TableRow>
                </TableHead>
            )}
        </>
    )
}

function PlaceHolderRow({ row, onSelectRow }) {
    const tableRowProps = onSelectRow
        ? {
              sx: { cursor: "pointer" },
              hover: true,
              onClick: () => onSelectRow(row),
          }
        : {}

    return (
        <TableRow>
            <TableRow {...tableRowProps}>
                {Object.entries(row).map(([key, value]) => (
                    <TableCell key={key}>{value}</TableCell>
                ))}
            </TableRow>
        </TableRow>
    )
}
